// Library Methods
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { TutorialVideoType } from "src/types/sharedTypes";

// Config
import { priorityLanguageMap } from "src/config/languages";

type FullVideoType = {
  selectedVideo: TutorialVideoType;
  startTime: number;
  shouldAutoplay: boolean;
  handleTimestampClick: (seconds: number) => void;
};

const FullVideo = ({
  selectedVideo,
  startTime,
  shouldAutoplay,
  handleTimestampClick,
}: FullVideoType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { surface } = getSurfaceTokens(theme);
  const currentLang = i18next.language as keyof typeof priorityLanguageMap;

  const videoUrl = useMemo(
    () => (currentLang === "fr" ? selectedVideo.frUrl : selectedVideo.url),
    [currentLang, selectedVideo.frUrl, selectedVideo.url]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          height: "fit-content",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            alignSelf: "flex-start",
            aspectRatio: 16 / 9,
            width: "100%",
            maxWidth: { xs: "100%", lg: "900px" },
            overflow: "hidden",
          }}
        >
          <iframe
            key={`${videoUrl}-${startTime}`}
            src={`${videoUrl}&rel=0&start=${startTime}&autoplay=${shouldAutoplay ? 1 : 0}`}
            title={selectedVideo.name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{
              borderRadius: "4px",
              objectFit: "cover",
              width: "100%",
              height: "100%",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxWidth: { xs: "100%", lg: "400px" },
            width: "100%",
            height: "auto",
            borderRadius: "4px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#4D5063" : surface,
            border: "1px solid #5B6D7A40",
            padding: "12px",
          }}
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {t("Chapters")}
          </Typography>
          {selectedVideo.timestamps.map((timestamp, index) => (
            <Box
              key={`${timestamp.label}-${index}`}
              role="button"
              sx={{
                display: "flex",
                gap: "8px",
                cursor: "pointer",
                width: "fit-content",
                fontWeight: 600,
              }}
              onClick={() => {
                handleTimestampClick(
                  currentLang === "fr"
                    ? timestamp.frSeconds ?? 0
                    : timestamp.seconds
                );
              }}
            >
              <Typography fontSize="14px" fontWeight={600} color="#E2772E">
                {currentLang === "fr" ? timestamp.frTime : timestamp.time}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {t(timestamp.label)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* TODO: show these values under the tutorial video */}
      {/* <Typography sx={{ fontWeight: 600 }}>{t(selectedVideo.name)}</Typography>
      <Typography>{t(selectedVideo.description)}</Typography> */}
    </Box>
  );
};

export default FullVideo;
